import React from 'react';
import AppBar from '@mui/material/AppBar';
import { IconButton, Tooltip } from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import { Link } from 'react-router-dom';
import { logout } from "../../api/Api";
import { useNavigate } from "react-router-dom";
import TwitterIcon from '@mui/icons-material/Twitter';
import LanguageIcon from '@mui/icons-material/Language';
import { useColors, useColorMode } from '../../theme/ColorModeContext';
import { RESEARCHZERO_URL, WEB3PWN_X_URL, DISCORD_INVITE_URL } from '../../shared/brand';
import { DiscordIcon, trackDiscordClick } from '../../shared/DiscordCTA';

interface PropsTopBar {
    token: string;
    username: string;
    setToken: React.Dispatch<React.SetStateAction<any>>;
    admin: boolean;
}

const TopBar = (props: PropsTopBar) => {
    const navigate = useNavigate();
    const token = props.token;
    const setToken = props.setToken;
    const username = props.username;
    const colors = useColors();
    const { mode, toggleColorMode } = useColorMode();

    const sxSocialLink = {
        textDecoration: "none",
        fontSize: "14px",
        fontWeight: 500,
        fontFamily: '"Exo 2", sans-serif',
        display: "flex",
        alignItems: "center",
        color: colors.text.secondary,
        padding: "8px 10px",
        borderRadius: "6px",
        transition: "all 0.2s ease",
        "&:hover": {
            color: colors.accent.cyan,
            backgroundColor: colors.accent.cyanGlow,
            transform: "translateY(-1px)",
        },
    };

    const handleLogout = async () => {
        logout(token);
        setToken("");
        navigate("/dashboard");
    }

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    }

    return (
        <AppBar
            color="transparent"
            elevation={0}
            sx={{
                backgroundColor: colors.bg.overlay,
                borderBottom: `1px solid ${colors.border.subtle}`,
                backdropFilter: "blur(24px) saturate(1.8)",
                WebkitBackdropFilter: "blur(24px) saturate(1.8)",
                boxShadow: "0 12px 36px rgba(0, 0, 0, 0.04)",
            }}>
            <Container maxWidth={false} sx={{ width: "95%", paddingLeft: "16px" }}>
            <Toolbar disableGutters sx={{ minHeight: '56px !important' }}>

            <Box sx={{ flexGrow: 1 }} />

            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Box
                    component="a"
                    href={RESEARCHZERO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        ...sxSocialLink,
                        gap: '8px',
                        display: { xs: 'none', md: 'flex' },
                    }}
                >
                    <LanguageIcon sx={{ fontSize: "18px" }} />
                    <span>ResearchZero</span>
                </Box>
                <Box component="a" href={DISCORD_INVITE_URL} onClick={() => trackDiscordClick('topbar')} target="_blank" rel="noopener noreferrer" aria-label="WEB3PWN Discord" sx={sxSocialLink}>
                    <DiscordIcon size={18} />
                </Box>
                <Box component="a" href={WEB3PWN_X_URL} target="_blank" rel="noopener noreferrer" aria-label="WEB3PWN X" sx={sxSocialLink}>
                    <TwitterIcon sx={{ fontSize: "18px" }} />
                </Box>

                <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                    <IconButton
                        onClick={toggleColorMode}
                        sx={{
                            color: colors.text.secondary,
                            borderRadius: "6px",
                            padding: "8px",
                            border: `1px solid transparent`,
                            transition: "all 0.2s ease",
                            "&:hover": {
                                color: colors.accent.cyan,
                                borderColor: colors.border.accent,
                                backgroundColor: colors.accent.cyanGlow,
                            },
                        }}
                    >
                        {mode === 'dark'
                            ? <LightModeIcon sx={{ fontSize: "18px" }} />
                            : <DarkModeIcon sx={{ fontSize: "18px" }} />
                        }
                    </IconButton>
                </Tooltip>
            </Box>

            <Box sx={{ flexGrow: 0, marginLeft: "12px" }}>
                <IconButton
                    size="large"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleMenu}
                    sx={{
                        fontSize: "14px",
                        fontFamily: '"Exo 2", sans-serif',
                        fontWeight: 500,
                        color: colors.text.primary,
                        borderRadius: "6px",
                        padding: "6px 12px",
                        border: `1px solid ${colors.border.subtle}`,
                        transition: "all 0.2s ease",
                        "&:hover": {
                            borderColor: colors.border.accent,
                            backgroundColor: colors.accent.cyanGlow,
                        },
                    }}
                >
                    {username}
                    <AccountCircle sx={{
                        marginLeft: "8px",
                        color: colors.accent.cyan,
                    }} />
                </IconButton>
                <Menu
                    id="menu-appbar"
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    sx={{ mt: "40px" }}
                >
                    <MenuItem onClick={handleClose}>
                        <Link to={"/settings"} style={{ color: colors.text.primary, textDecoration: "none" }}>
                            Settings
                        </Link>
                    </MenuItem>
                    <MenuItem onClick={handleLogout} style={{ color: colors.text.primary }}>Logout</MenuItem>
                </Menu>
            </Box>
            </Toolbar>
            </Container>
        </AppBar>
    )
}

export default TopBar
